import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import settings from "../../apiConfigs/settings";
import { ReactComponent as Edit } from "../../assets/edit.svg";
import Dropdown from "../../designComponents/Dropdown/Dropdown";
import LabelInputField from "../../designComponents/LabelInputField/LabelInputField";
import Modal from "../../designComponents/Modal";
import requestAPI from "../../utils/requestAPI";
import {
  AcceptButton,
  ButtonsSection,
  LabelText,
  RejectButton,
  SectionTitle,
  StyledCard,
  ValueText,
} from "../Properties/Details/styles";

type AddCategoryModalProps = {
  visible: "add" | "edit" | null;
  onClose: () => void;
  handleSave: ({
    data,
    mode,
  }: {
    data: { ratedResource: string; categories: string[] };
    mode: "add" | "edit";
  }) => void;
  defaultData?: { _id?: string; ratedResource: string; categories: string[] };
};

const AddCategoryModal = ({
  visible,
  onClose,
  handleSave,
  defaultData,
}: AddCategoryModalProps) => {
  const [categoryInformation, setCategoryInformation] = useState({
    ratedResource: "Property",
    categories: [""],
  });

  useEffect(() => {
    if (visible === "edit" && defaultData) {
      setCategoryInformation(defaultData);
    } else if (visible === "add") {
      setCategoryInformation({ ratedResource: "Property", categories: [""] });
    }
  }, [visible, defaultData]);

  const handleCategoryChange = (index: number, value: string) => {
    const updated = [...categoryInformation.categories];
    updated[index] = value;
    setCategoryInformation((prev) => ({ ...prev, categories: updated }));
  };

  const addCategoryField = () => {
    setCategoryInformation((prev) => ({
      ...prev,
      categories: [...prev.categories, ""],
    }));
  };

  return (
    <Modal
      isOpen={visible !== null}
      onClose={onClose}
      title={`${visible === "edit" ? "Edit" : "Add"} Rating Category`}
    >
      <div style={{ padding: "1rem" }}>
        <Dropdown
          value={categoryInformation.ratedResource}
          options={[
            { label: "Property", value: "Property" },
            { label: "Tenant", value: "Tenant" },
            { label: "Landlord", value: "Landlord" },
            { label: "Service Provider", value: "ServiceProvider" },
            { label: "Service Creator", value: "ServiceCreator" },
          ]}
          onChange={(e) =>
            setCategoryInformation({
              ...categoryInformation,
              ratedResource: e.target.value,
            })
          }
        />
      </div>

      <div
        style={{
          padding: "1rem",
          display: "flex",
          flexDirection: "row",
          gap: 10,
        }}
      >
        {categoryInformation.categories.map((cat, index) => (
          <div key={index} style={{ marginBottom: "10px" }}>
            <LabelInputField
              name="text"
              label={`Category ${index + 1}`}
              value={cat}
              onChange={(e) => handleCategoryChange(index, e.target.value)}
              style={{
                label: {
                  fontSize: "14px",
                  fontWeight: "500px",
                  color: "#555A77",
                },
                input: {
                  height: 50,
                  border: "1px solid #F1EFFB",
                  padding: 8,
                  background: "#F1EFFB",
                  borderRadius: 6,
                  width: "100%",
                },
                container: {
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  paddingTop: 14,
                },
              }}
            />
          </div>
        ))}
      </div>

      <AcceptButton onClick={addCategoryField} style={{ marginTop: "10px" }}>
        + Add Category Field
      </AcceptButton>

      <ButtonsSection style={{ marginTop: "20px" }}>
        <AcceptButton
          onClick={() => {
            handleSave({
              data: categoryInformation,
              mode: visible!,
            });
            onClose();
          }}
        >
          Save
        </AcceptButton>
        <RejectButton onClick={onClose}>Cancel</RejectButton>
      </ButtonsSection>
    </Modal>
  );
};

function RatingCategories() {
  const [editItem, setEditItem] = useState<{
    _id?: string;
    ratedResource: string;
    categories: string[];
  } | null>(null);

  const handleSave = async ({
    data,
    mode,
  }: {
    data: {
      ratedResource: string;
      categories: string[];
    };
    mode: "add" | "edit";
  }) => {
    try {
      if (mode === "add") {
        await requestAPI(settings.addRatingCategory(data));
      } else {
        await requestAPI(
          settings.updateRatingCategory(data, editItem?._id || "")
        );
      }
      toast.success("Successfully saved");
      getData();
    } catch (error) {
      toast.error("Error saving category");
    }
  };

  const getData = async () => {
    const response = await requestAPI(settings.getRatingCategory());
    setData(response);
  };

  const [visible, setVisible] = useState<"add" | "edit" | null>(null);
  const [data, setData] = useState<
    {
      ratedResource: string;
      categories: string[];
    }[]
  >([]);

  useEffect(() => {
    getData();
  }, []);

  return (
    <StyledCard style={{ margin: 0 }}>
      <ButtonsSection>
        <SectionTitle>Rating Categories</SectionTitle>
        <AcceptButton
          onClick={() => {
            setVisible("add");
          }}
        >
          Add Category
        </AcceptButton>
      </ButtonsSection>

      <div>
        {data?.length > 0 ? (
          data?.map((item, index) => (
            <>
              <div
                key={index}
                style={{
                  marginBottom: "1rem",
                  backgroundColor: "#f0f8ff",
                  padding: "2rem",
                  borderRadius: "1rem",
                }}
              >
                <div
                  style={{
                    flexDirection: "row",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <LabelText>Rated Resource: {item.ratedResource}</LabelText>
                  <Edit
                    fill="blue"
                    onClick={() => {
                      setEditItem(item);
                      setVisible("edit");
                    }}
                  />
                </div>
                {item.categories.map((category, idx) => (
                  <ValueText key={idx}>- {category}</ValueText>
                ))}
              </div>
            </>
          ))
        ) : (
          <p>No rating categories available.</p>
        )}
      </div>

      {/* Modal */}
      <AddCategoryModal
        handleSave={handleSave}
        visible={visible}
        onClose={() => {
          setVisible(null);
          setEditItem(null);
        }}
        defaultData={editItem || undefined}
      />
    </StyledCard>
  );
}

export default RatingCategories;
